import { useCallback, useEffect, useRef, useState } from 'react'
import { pointerAngle, rotationAfterDrag } from '../utils/canvasRotation'

/**
 * Ab wie vielen Pixeln ein Druck ein **Zug** ist.
 *
 * Die Zahl trennt zwei Bedienungen, die auf demselben Element liegen: Ziehen
 * stellt die **Neigung**, Klicken tippt den **Klebestreifen** an. Beide Irrtümer
 * ärgern, und zwar verschieden: Gilt ein Zittern als Zug, öffnet der Klick die
 * Options-Leiste nicht — und er ist der einzige Weg hinein. Gilt ein Zug als
 * Klick, klappt sie mitten in der Geste zu.
 *
 * **Sechs Pixel**, gemessen am Griff: Der Streifen wird gut 100 px von seiner
 * Mitte gefasst, sechs Pixel sind dort rund 3,5° — eine halbe Raste. Darunter
 * ist es Zittern, darüber eine Absicht.
 */
const DRAG_THRESHOLD_PX = 6

export interface UseRotateDragOptions {
  /**
   * Wo die **Mitte** des gedrehten Elements liegt — in BILDSCHIRM-Koordinaten,
   * frisch beim Anfassen gemessen.
   *
   * Als Funktion und nicht als Wert: Das Element kann zwischen zwei Zügen
   * verschoben, skaliert oder gezoomt worden sein.
   */
  getCenter: () => { x: number; y: number } | null
  /** Die erlaubten Stufen — beim Zettel `canvasNoteRotations()`, beim Streifen `canvasTapeAngles(spot)`. */
  getRasten: () => number[]
  /** Der Winkel, mit dem der Zug beginnt. */
  getStart: () => number
  /**
   * Der Zug ist durch, und die Raste hat sich geändert.
   *
   * Wird **einmal** je Zug gerufen, beim Loslassen — nicht je Raste: Wer durch
   * acht Stufen fährt, will danach ein ⌘Z und nicht acht.
   */
  onCommit: (angle: number) => void
  /** Doppelklick auf die Zone — zurück auf gerade. Fehlt er, tut der Doppelklick nichts. */
  onReset?: () => void
  /** Aus, solange gar nicht gedreht werden darf (Hand-Werkzeug, Bearbeiten-Modus, Abziehen). */
  enabled?: boolean
}

export interface RotateDrag {
  /** Ob gerade gedreht wird — daran hängt, dass die Ziehpunkte weichen. */
  rotating: boolean
  /**
   * Die Raste, die der Zug GERADE zeigt, oder `null` außerhalb eines Zugs.
   *
   * Sie überlebt das Loslassen: Sie fällt erst, wenn die Zeile sich bewegt
   * (siehe unten). Wer sie beim `pointerup` wegnähme, sähe den alten Winkel
   * für die Frames bis zum Rücklauf der Live-Query.
   */
  preview: number | null
  /** Auf das Element hängen, das die Geste annimmt. */
  handlers: {
    onPointerDown: (event: React.PointerEvent) => void
    onPointerMove: (event: React.PointerEvent) => void
    onPointerUp: (event: React.PointerEvent) => void
    onPointerCancel: (event: React.PointerEvent) => void
    onDoubleClick: (event: React.MouseEvent) => void
  }
  /**
   * Ob der letzte Druck ein ZUG war — für einen Aufrufer, der aus demselben
   * Druck auch einen Klick machen könnte (der **Klebestreifen** tippt damit
   * an). Nach dem Lesen zurückgesetzt.
   */
  consumeDragged: () => boolean
}

/**
 * Die **Neigung ziehen** — die Geste, die Zettel, Memo und **Klebestreifen**
 * gemeinsam benutzen.
 *
 * Der Zeiger drückt, kreist um die **Mitte** des Elements und lässt los; die
 * Neigung rastet dabei durch die Stufen, die der Aufrufer vorgibt. Siehe
 * „Neigung" in CONTEXT.md, ADR 0015 und
 * `.claude/tasks/canvas-neigung-drehen.md`.
 *
 * **Drei Dinge, die schon am Streifen gelernt wurden** und hier nur an einer
 * Stelle stehen:
 *
 * 1. **`setPointerCapture`** — nach wenigen Grad ist der Zeiger längst neben
 *    der kleinen Zone; ohne die Aufnahme risse der Zug dort ab.
 * 2. **Vorschau im Zustand, geschrieben beim Loslassen** — EIN **Schritt** je
 *    Zug.
 * 3. **Die Zeile hat das letzte Wort** — die Vorschau fällt, sobald der
 *    gespeicherte Winkel sich bewegt, und zwar egal WOHIN: Ein ⌘Z gleich nach
 *    dem Zug bringt den alten Winkel zurück, und eine Vorschau, die am
 *    gezogenen Wert klebte, zeigte für immer eine Stufe, die es nicht mehr
 *    gibt.
 *
 * **Bildschirm-Grad, nicht Flächen-Grad:** Ein Winkel ändert sich beim Zoomen
 * nicht, hier wird also nichts durch den Zoom geteilt.
 */
export function useRotateDrag({
  getCenter,
  getRasten,
  getStart,
  onCommit,
  onReset,
  enabled = true,
}: UseRotateDragOptions): RotateDrag {
  const dragRef = useRef<
    { center: { x: number; y: number }; from: number; at: number; down: { x: number; y: number } } | null
  >(null)
  const [preview, setPreview] = useState<number | null>(null)
  const [rotating, setRotating] = useState(false)
  /*
    Ob der letzte Druck ein ZUG war — der Streifen macht daraus sonst einen
    Klick, der ihn antippt.

    **Am Weg des Zeigers, nicht an der Raste.** Bis September 2026 wurde der
    Merker erst beim Schreiben gesetzt; eine Geste, die nichts verstellte, galt
    damit als Klick. Der häufigste Fall ist der **Anschlag**: Wer den Streifen
    an der äußersten Stufe packt und weiter in diese Richtung schwenkt, bleibt
    auf ihr stehen — und bekam dafür seine Options-Leiste zugeklappt.
    (`.claude/tasks/zug-am-anschlag-bleibt-zug.md`)
  */
  const draggedRef = useRef(false)

  const handlePointerDown = useCallback((event: React.PointerEvent) => {
    if (!enabled) return
    const center = getCenter()
    if (!center) return
    // Nur die Haupttaste: Rechtsklick und Mittelklick sind hier nichts.
    if (event.button !== 0) return
    dragRef.current = {
      center,
      from: getStart(),
      at: pointerAngle(center, { x: event.clientX, y: event.clientY }),
      down: { x: event.clientX, y: event.clientY },
    }
    draggedRef.current = false
    setRotating(true)
    event.currentTarget.setPointerCapture?.(event.pointerId)
    // Der Druck gehört der Drehung — React Flow soll daraus weder ein
    // Verschieben des Nodes noch ein Fahren des Ausschnitts machen.
    event.stopPropagation()
  }, [enabled, getCenter, getStart])

  const handlePointerMove = useCallback((event: React.PointerEvent) => {
    const zug = dragRef.current
    if (!zug) return
    /*
      Ab hier ist es ein Zug. Die Schwelle trennt Ziehen von Klicken: Ein
      ruhiger Klick zittert um ein, zwei Pixel, jede gewollte Geste liegt weit
      darüber. Gemessen am DRUCKPUNKT, nicht am letzten Bild — sonst käme man
      auch durch langsames Schleichen nie über die Schwelle.
    */
    if (Math.hypot(event.clientX - zug.down.x, event.clientY - zug.down.y) > DRAG_THRESHOLD_PX) {
      draggedRef.current = true
    }
    setPreview(rotationAfterDrag(
      zug.from,
      zug.at,
      pointerAngle(zug.center, { x: event.clientX, y: event.clientY }),
      getRasten(),
    ))
  }, [getRasten])

  const endDrag = useCallback((event: React.PointerEvent) => {
    const zug = dragRef.current
    if (!zug) return
    dragRef.current = null
    setRotating(false)
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    const ziel = rotationAfterDrag(
      zug.from,
      zug.at,
      pointerAngle(zug.center, { x: event.clientX, y: event.clientY }),
      getRasten(),
    )
    /*
      **Die Vorschau bleibt stehen** — sie fällt erst, wenn die Zeile spricht
      (siehe der Effekt unten). Nähme man sie hier weg, zeigte das Element für
      die Frames bis zum Rücklauf der Live-Query wieder die ALTE Stufe: Es
      spränge zurück und gleich darauf vor.
    */
    // Eine Geste, die nichts verstellt, schreibt auch nichts — ein Zug war sie
    // trotzdem, wenn der Zeiger weit genug lief (siehe `handlePointerMove`).
    if (ziel === zug.from) return
    /*
      Umgekehrt zählt auch ein KURZER Weg, der die Raste bewegt: Wer den
      Streifen dicht an seiner Mitte greift, verstellt ihn mit wenigen Pixeln.
      Die Schwelle allein ließe so eine Geste als Klick durchgehen.
    */
    draggedRef.current = true
    onCommit(ziel)
  }, [getRasten, onCommit])

  /*
    **Die Zeile hat das letzte Wort.** Sobald der gespeicherte Winkel sich
    bewegt, hat die Vorschau ausgedient — dasselbe Muster wie beim Streifen und
    beim **Ablösen**: Zwei Zustände, die dieselbe Sache erzählen, dürfen nicht
    aneinander stoßen, sie müssen sich überlappen.
  */
  const gespeichert = getStart()
  useEffect(() => { setPreview(null) }, [gespeichert])

  const handleDoubleClick = useCallback((event: React.MouseEvent) => {
    if (!enabled || !onReset) return
    // Ohne das liefe der Doppelklick als Node-Doppelklick weiter und öffnete
    // den Bearbeiten-Modus — die Zone liegt zwar neben dem Papier, ihr
    // Ereignis steigt aber durch dieselbe Node-Hülle auf.
    event.stopPropagation()
    event.preventDefault()
    onReset()
  }, [enabled, onReset])

  const consumeDragged = useCallback(() => {
    const war = draggedRef.current
    draggedRef.current = false
    return war
  }, [])

  return {
    rotating,
    preview,
    consumeDragged,
    handlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onDoubleClick: handleDoubleClick,
    },
  }
}
